import { FC, useMemo, useState, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import clsx from 'clsx';

import { Modal, VirtualizedTable } from '@/components';
import { getReportByIdQuery } from '@/query';
import { CreditUnionRow } from '@/types';

import { ListViewModalProps } from './types';

import styles from './styles.module.scss';

export const ListViewModal: FC<ListViewModalProps> = ({ open, handleClose, selectedListItem }) => {
  const { data: tableData, isLoading } = useQuery(getReportByIdQuery(selectedListItem?.id));
  const [sorting, setSorting] = useState<SortingState>([]);

  const columnHelper = createColumnHelper<CreditUnionRow>();

  const data: CreditUnionRow[] = useMemo(() => tableData?.data || [], [tableData?.data]);

  useEffect(() => {
    if (!open) {
      setSorting([]);
    }
  }, [open, selectedListItem?.id]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Credit Union',
        enableSorting: true,
        size: 400,
      }),
      columnHelper.accessor('city', {
        header: 'City',
        enableSorting: true,
        size: 150,
      }),
      columnHelper.accessor('state', {
        header: 'State',
        enableSorting: true,
        size: 100,
      }),
      columnHelper.accessor('joinNumber', {
        header: 'Join Number',
        enableSorting: true,
        size: 150,
      }),
      columnHelper.accessor('assets', {
        header: 'Assets',
        enableSorting: true,
        cell: (info) => `$${info.getValue().toLocaleString()}`,
        size: 150,
      }),
    ],
    [],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const includesListWord = useMemo(() => selectedListItem?.name?.toLowerCase().includes('list'), [selectedListItem]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${selectedListItem?.name} ${!includesListWord ? 'List' : ''} View`}
    >
      <VirtualizedTable table={table} isLoading={isLoading} filtersApplied={false} />

      <div className={styles.modalActionsContainer}>
        <button className={clsx(styles.button, styles.submitBtn)} onClick={handleClose}>
          Close
        </button>
      </div>
    </Modal>
  );
};
